import { Suspense, useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Activity,
  AlarmClock,
  Check,
  ChevronsUpDown,
  FlaskConical,
  Inbox,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  Monitor,
  Moon,
  PanelLeft,
  PanelLeftClose,
  Plug,
  Plus,
  Settings,
  Sun,
  UserRound,
  Users,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { ROLE_LABELS } from '@onesource/shared';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { sessionKey, useSession, useWorkspace } from '@/hooks/session';
import { useTheme } from '@/hooks/theme';
import { Avatar, Skeleton } from './ui/display';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
} from './ui/overlays';
import { ErrorBoundary } from './common';
import { NotificationsBell } from './notifications';
import { useSmartOperations } from '@/hooks/features';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  count?: number;
}

function useNavCounts(workspaceId: string) {
  const leads = useQuery({
    queryKey: ['ws', workspaceId, 'leads', 'nav'],
    queryFn: () => api.get<{ unread: number }>(`/api/workspaces/${workspaceId}/leads?limit=1`),
    refetchInterval: 60_000,
  });
  const customers = useQuery({
    queryKey: ['ws', workspaceId, 'customers', 'nav'],
    queryFn: () => api.get<{ pendingDuplicates: number }>(`/api/workspaces/${workspaceId}/customers?limit=1`),
    refetchInterval: 120_000,
  });
  return { unread: leads.data?.unread ?? 0, duplicates: customers.data?.pendingDuplicates ?? 0 };
}

function Logo({ collapsed }: { collapsed?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <svg viewBox="0 0 32 32" className="size-6 shrink-0" aria-hidden>
        <rect width="32" height="32" rx="8" className="fill-accent" />
        <circle cx="16" cy="16" r="7" fill="none" stroke="white" strokeWidth="3" />
        <circle cx="16" cy="16" r="2.5" fill="white" />
      </svg>
      {!collapsed && <span className="text-[15px] font-semibold tracking-tight">Knownlead</span>}
    </div>
  );
}

function WorkspaceSwitcher({ collapsed }: { collapsed: boolean }) {
  const { workspace } = useWorkspace();
  const { data: session } = useSession();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'flex w-full items-center gap-2 rounded-md border border-border bg-surface px-2 py-1.5 text-left shadow-xs hover:bg-hover',
            collapsed && 'justify-center px-0',
          )}
          aria-label={`Workspace: ${workspace.name}. Switch workspace`}
        >
          <span className="flex size-6 shrink-0 items-center justify-center rounded bg-surface-2 text-[11px] font-semibold">
            {workspace.name.slice(0, 1).toUpperCase()}
          </span>
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-medium">{workspace.name}</span>
                <span className="block text-[11px] text-muted">
                  {ROLE_LABELS[workspace.role]}
                  {workspace.mode === 'demo' && ' · Demo'}
                </span>
              </span>
              <ChevronsUpDown className="size-3.5 text-muted" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
        {session?.workspaces.map((w) => (
          <DropdownMenuItem key={w.id} onSelect={() => navigate(`/w/${w.id}/inbox`)}>
            <span className="flex size-5 items-center justify-center rounded bg-surface-2 text-[10px] font-semibold">{w.name.slice(0, 1).toUpperCase()}</span>
            <span className="min-w-0 flex-1 truncate">{w.name}</span>
            {w.mode === 'demo' && <FlaskConical className="!size-3.5" />}
            {w.id === workspace.id && <Check className="!text-accent" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/onboarding')}>
          <Plus />
          Create workspace
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu({ collapsed }: { collapsed: boolean }) {
  const { data: session } = useSession();
  const { base } = useWorkspace();
  const { theme, setTheme } = useTheme();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const user = session?.user;
  const signOut = async () => {
    await api.post('/api/auth/signout');
    qc.setQueryData(sessionKey, null);
    qc.clear();
    navigate('/signin');
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={cn('flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-hover', collapsed && 'justify-center px-0')} aria-label="Account menu">
          <Avatar name={user?.name} size={24} />
          {!collapsed && <span className="min-w-0 flex-1 truncate text-[13px]">{user?.name}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="w-60">
        <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => navigate(`${base}/settings/profile`)}>
          <UserRound />
          Profile & password
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Theme</DropdownMenuLabel>
        {(
          [
            ['light', 'Light', Sun],
            ['dark', 'Dark', Moon],
            ['system', 'System', Monitor],
          ] as const
        ).map(([value, label, Icon]) => (
          <DropdownMenuItem key={value} onSelect={() => setTheme(value)}>
            <Icon />
            <span className="flex-1">{label}</span>
            {theme === value && <Check className="!text-accent" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void signOut()}>
          <LogOut />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NavLinks({ items, collapsed, onNavigate }: { items: NavItem[]; collapsed: boolean; onNavigate?: () => void }) {
  return (
    <ul className="space-y-0.5">
      {items.map((item) => {
        const link = (
          <NavLink
            to={item.to}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'group flex h-8 items-center gap-2.5 rounded-md px-2 text-[13px] font-medium text-muted transition-colors hover:bg-hover hover:text-foreground',
                isActive && 'bg-hover text-foreground',
                collapsed && 'justify-center px-0',
              )
            }
          >
            <item.icon className="size-4 shrink-0" />
            {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
            {!collapsed && !!item.count && (
              <span className="rounded bg-accent-soft px-1.5 text-[11px] font-semibold leading-5 text-accent">{item.count > 99 ? '99+' : item.count}</span>
            )}
            {collapsed && !!item.count && <span className="absolute ml-5 -mt-4 size-1.5 rounded-full bg-accent" />}
          </NavLink>
        );
        return <li key={item.to}>{collapsed ? <Tooltip content={item.label} side="right">{link}</Tooltip> : link}</li>;
      })}
    </ul>
  );
}

function SidebarBody({ collapsed, onNavigate, toggle }: { collapsed: boolean; onNavigate?: () => void; toggle?: ReactNode }) {
  const { base, workspace, can } = useWorkspace();
  const counts = useNavCounts(workspace.id);
  const smartOps = useSmartOperations();
  const main: NavItem[] = [
    { to: `${base}/overview`, label: 'Overview', icon: LayoutDashboard },
    { to: `${base}/inbox`, label: 'Inbox', icon: Inbox, count: counts.unread },
    ...(smartOps ? [{ to: `${base}/follow-ups`, label: 'Follow-ups', icon: AlarmClock }] : []),
    { to: `${base}/customers`, label: 'Customers', icon: Users, count: counts.duplicates },
    { to: `${base}/leads`, label: 'Leads', icon: ListChecks },
    { to: `${base}/channels`, label: 'Channels', icon: Plug },
    { to: `${base}/automations`, label: 'Automations', icon: Zap },
    { to: `${base}/activity`, label: 'Activity', icon: Activity },
  ];
  const secondary: NavItem[] = [
    { to: `${base}/team`, label: 'Team', icon: UserRound },
    ...(can('workspace.read') ? [{ to: `${base}/settings`, label: 'Settings', icon: Settings }] : []),
  ];
  return (
    <div className="flex h-full flex-col gap-3 p-2.5">
      <div className={cn('flex h-8 items-center justify-between px-1', collapsed && 'justify-center px-0')}>
        <Logo collapsed={collapsed} />
        {!collapsed && toggle}
      </div>
      <WorkspaceSwitcher collapsed={collapsed} />
      <nav aria-label="Main" className="flex min-h-0 flex-1 flex-col justify-between overflow-y-auto scrollbar-thin">
        <NavLinks items={main} collapsed={collapsed} onNavigate={onNavigate} />
        <div className="space-y-2 pt-4">
          <NotificationsBell collapsed={collapsed} />
          <NavLinks items={secondary} collapsed={collapsed} onNavigate={onNavigate} />
          {collapsed && toggle}
          <div className="border-t border-border pt-2">
            <UserMenu collapsed={collapsed} />
          </div>
        </div>
      </nav>
    </div>
  );
}

export function AppShell() {
  const { workspace } = useWorkspace();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('os-sidebar') === 'collapsed';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => setMobileOpen(false), [location.pathname]);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem('os-sidebar', next ? 'collapsed' : 'expanded');
    } catch {
      /* ignore */
    }
  };
  const toggle = (
    <Tooltip content={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} side="right">
      <Button variant="ghost" size="icon-sm" onClick={toggleCollapsed} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} className={cn(collapsed && 'mx-auto flex')}>
        {collapsed ? <PanelLeft /> : <PanelLeftClose />}
      </Button>
    </Tooltip>
  );

  return (
    <div className="flex h-full">
      <aside className={cn('hidden shrink-0 border-r border-border bg-background transition-[width] md:block', collapsed ? 'w-14' : 'w-60')}>
        <SidebarBody collapsed={collapsed} toggle={toggle} />
      </aside>

      <DialogPrimitive.Root open={mobileOpen} onOpenChange={setMobileOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/40 md:hidden" />
          <DialogPrimitive.Content className="fixed inset-y-0 left-0 z-50 w-72 border-r border-border bg-background shadow-xl md:hidden" aria-describedby={undefined}>
            <DialogPrimitive.Title className="sr-only">Navigation</DialogPrimitive.Title>
            <SidebarBody collapsed={false} onNavigate={() => setMobileOpen(false)} />
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-12 items-center gap-2 border-b border-border px-3 md:hidden">
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} aria-label="Open navigation">
            <Menu />
          </Button>
          <Logo />
          <span className="ml-auto max-w-[40%] truncate text-[13px] text-muted">{workspace.name}</span>
          <NotificationsBell variant="icon" />
        </div>
        {workspace.mode === 'demo' && (
          <div className="flex items-center gap-2 border-b border-warning/30 bg-warning-soft px-4 py-1.5 text-[12px] text-warning">
            <FlaskConical className="size-3.5 shrink-0" />
            <span>
              <strong className="font-semibold">Demo workspace.</strong> Customers, messages and channels here are simulated and never come from or go to a real account.
            </span>
          </div>
        )}
        <main className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
          <ErrorBoundary key={location.pathname}>
            <Suspense fallback={<div className="space-y-3 p-6"><Skeleton className="h-6 w-48" /><Skeleton className="h-32 w-full" /></div>}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
